// API Client for DevFactory Studio
import { config } from '../config';

// Types
export interface App {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  status: 'draft' | 'published' | 'archived';
  version: number;
  config: string;
  created_at: string;
  updated_at: string;
}

export interface Workflow {
  id: string;
  app_id: string;
  name: string;
  description: string;
  trigger: string;
  steps: string;
  settings: string;
  status: 'draft' | 'active' | 'paused';
  version: number;
  created_at: string;
  updated_at: string;
}

export interface Form {
  id: string;
  app_id: string;
  name: string;
  slug: string;
  description: string;
  schema_id: string | null;
  layout: string;
  settings: string;
  created_at: string;
  updated_at: string;
}

export interface Schema {
  id: string;
  app_id: string;
  name: string;
  slug: string;
  description: string;
  fields: string;
  created_at: string;
  updated_at: string;
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
  };
}

interface ListResponse<T> {
  items: T[];
  total: number;
}

// Helper function for API calls
async function apiCall<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = localStorage.getItem('accessToken');

  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });

  const data: ApiResponse<T> = await response.json();

  if (!data.success || !response.ok) {
    throw new Error(data.error?.message || 'API request failed');
  }

  return data.data as T;
}

// Apps API
export const appsApi = {
  list: () => apiCall<ListResponse<App>>('/api/v1/studio/apps'),

  get: (id: string) => apiCall<App>(`/api/v1/studio/apps/${id}`),

  create: (data: Partial<App>) =>
    apiCall<App>('/api/v1/studio/apps', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<App>) =>
    apiCall<App>(`/api/v1/studio/apps/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiCall<void>(`/api/v1/studio/apps/${id}`, {
      method: 'DELETE',
    }),
};

// Workflows API
export const workflowsApi = {
  list: (appId?: string) => {
    const query = appId ? `?appId=${appId}` : '';
    return apiCall<ListResponse<Workflow>>(`/api/v1/studio/workflows${query}`);
  },

  get: (id: string) => apiCall<Workflow>(`/api/v1/studio/workflows/${id}`),

  create: (data: Partial<Workflow>) =>
    apiCall<Workflow>('/api/v1/studio/workflows', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Workflow>) =>
    apiCall<Workflow>(`/api/v1/studio/workflows/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiCall<void>(`/api/v1/studio/workflows/${id}`, {
      method: 'DELETE',
    }),
};

// Forms API
export const formsApi = {
  list: (appId?: string) => {
    const query = appId ? `?appId=${appId}` : '';
    return apiCall<ListResponse<Form>>(`/api/v1/studio/forms${query}`);
  },

  get: (id: string) => apiCall<Form>(`/api/v1/studio/forms/${id}`),

  create: (data: Partial<Form>) =>
    apiCall<Form>('/api/v1/studio/forms', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Form>) =>
    apiCall<Form>(`/api/v1/studio/forms/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiCall<void>(`/api/v1/studio/forms/${id}`, {
      method: 'DELETE',
    }),
};

// Schemas API
export const schemasApi = {
  list: (appId?: string) => {
    const query = appId ? `?appId=${appId}` : '';
    return apiCall<ListResponse<Schema>>(`/api/v1/studio/schemas${query}`);
  },

  get: (id: string) => apiCall<Schema>(`/api/v1/studio/schemas/${id}`),

  create: (data: Partial<Schema>) =>
    apiCall<Schema>('/api/v1/studio/schemas', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  update: (id: string, data: Partial<Schema>) =>
    apiCall<Schema>(`/api/v1/studio/schemas/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  delete: (id: string) =>
    apiCall<void>(`/api/v1/studio/schemas/${id}`, {
      method: 'DELETE',
    }),
};
